/**
 * PublicShopPage — public shop landing page with 3D viewer entry points.
 * Route: /shop/:shopId
 *
 * Shows shop info (name, category, address, poster) and provides:
 *  - "Enter Shop" button → opens 3D viewer with best section
 *  - Per-section "View" buttons → opens 3D viewer for that specific section
 *
 * Works with all shop ID formats (shop_xxx, VID_xxx, legacy IDs).
 * The 3D viewer's loadDataset() resolves shop DB IDs → dataset IDs via
 * the d.shopId fallback in appStore.ts.
 */
import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore, getAuthHeaders } from '../../stores/authStore';
import { apiUrl } from '../../config';
import { isPushSupported, isSubscribed, subscribeCustomerPush } from '../../utils/pushNotifications';
import './PublicShopPage.css';

interface PublicShop {
  id: string;
  name: string;
  address: string;
  category: string;
  sections: { sectionId: string; name: string; order: number }[];
  createdAt: string;
}

interface DatasetInfo {
  id: string;
  shopId?: string;
  sectionId?: string;
  sectionName?: string;
  camera_count: number;
}

export default function PublicShopPage() {
  const { shopId } = useParams<{ shopId: string }>();
  const { t } = useTranslation('shops');
  const user = useAuthStore((s) => s.user);
  const [shop, setShop] = useState<PublicShop | null>(null);
  const [datasets, setDatasets] = useState<DatasetInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [posterError, setPosterError] = useState(false);
  const [following, setFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);

  // Fetch shop data and datasets in parallel
  useEffect(() => {
    if (!shopId) return;
    setLoading(true);

    const shopFetch = fetch(apiUrl(`/api/shops/public/${shopId}`))
      .then((res) => {
        if (!res.ok) throw new Error(res.status === 404 ? 'Shop not found' : `Error ${res.status}`);
        return res.json();
      });

    const datasetsFetch = fetch(apiUrl('/api/datasets'))
      .then((res) => (res.ok ? res.json() : { datasets: [] }))
      .then((manifest) => {
        // Filter datasets that belong to this shop:
        // Match by shopId field OR by dataset id starting with the shopId
        const shopDatasets: DatasetInfo[] = (manifest.datasets || []).filter(
          (d: DatasetInfo) => d.shopId === shopId || d.id === shopId
        );
        return shopDatasets;
      })
      .catch(() => [] as DatasetInfo[]);

    Promise.all([shopFetch, datasetsFetch])
      .then(([shopData, shopDatasets]) => {
        setShop(shopData);
        setDatasets(shopDatasets);
        setError(null);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [shopId]);

  // Check follow status and follower count
  useEffect(() => {
    if (!shopId) return;
    // Follower count (public)
    fetch(apiUrl(`/api/shops/${shopId}/followers/count`))
      .then((r) => r.ok ? r.json() : { count: 0 })
      .then((d) => setFollowerCount(d.count))
      .catch(() => {});
    // Following status (auth required)
    if (user) {
      fetch(apiUrl(`/api/shops/${shopId}/following`), { headers: { ...getAuthHeaders() } })
        .then((r) => r.ok ? r.json() : { following: false })
        .then((d) => setFollowing(d.following))
        .catch(() => {});
    }
  }, [shopId, user]);

  const handleFollowToggle = useCallback(async () => {
    if (!shopId || !user) return;
    setFollowLoading(true);
    try {
      if (following) {
        await fetch(apiUrl(`/api/shops/${shopId}/follow`), {
          method: 'DELETE',
          headers: { ...getAuthHeaders() },
        });
        setFollowing(false);
        setFollowerCount((c) => Math.max(0, c - 1));
      } else {
        // Ensure push subscription exists for this user
        if (isPushSupported()) {
          const hasSub = await isSubscribed();
          if (!hasSub) {
            await subscribeCustomerPush();
          }
        }
        await fetch(apiUrl(`/api/shops/${shopId}/follow`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        });
        setFollowing(true);
        setFollowerCount((c) => c + 1);
      }
    } catch (err) {
      console.error('[Follow] Error:', err);
    } finally {
      setFollowLoading(false);
    }
  }, [shopId, user, following]);

  // Check if this shop has any 3D data available
  const hasViewerData = datasets.length > 0;

  // Build a map of sectionId → datasetId for per-section "View" buttons
  const sectionDatasetMap = new Map<string, string>();
  for (const ds of datasets) {
    if (ds.sectionId) {
      const existing = sectionDatasetMap.get(ds.sectionId);
      if (!existing) {
        sectionDatasetMap.set(ds.sectionId, ds.id);
      }
    }
  }

  const handleEnterShop = () => {
    // Use shopId directly — loadDataset resolves shop DB IDs via d.shopId fallback
    window.location.href = `/?shopid=${shopId}`;
  };

  const handleViewSection = (datasetId: string) => {
    window.location.href = `/?shopid=${datasetId}`;
  };

  if (loading) {
    return (
      <div className="public-shop-page">
        <div className="public-shop-loading">{t('loadingShop')}</div>
      </div>
    );
  }

  if (error || !shop) {
    return (
      <div className="public-shop-page">
        <div className="public-shop-error">
          <h2>{t('shopNotFound')}</h2>
          <p>{error || t('shopNotFoundDescription')}</p>
          <Link to="/" className="public-shop-cta">{t('common:backToHome')}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="public-shop-page">
      <div className="public-shop-card">
        {/* Poster image */}
        {!posterError && (
          <div className="public-shop-poster">
            <img
              src={apiUrl(`/api/shops/${shop.id}/poster`)}
              alt={shop.name}
              className="public-shop-poster-img"
              onError={() => setPosterError(true)}
            />
          </div>
        )}

        <h1 className="public-shop-name">{shop.name}</h1>
        {shop.category && <span className="public-shop-category">{shop.category}</span>}
        {shop.address && <p className="public-shop-address">{shop.address}</p>}

        {/* Follow button */}
        <div className="public-shop-follow-row">
          {user ? (
            <button
              className={`public-shop-follow-btn ${following ? 'following' : ''}`}
              onClick={handleFollowToggle}
              disabled={followLoading}
            >
              {following ? `${t('following')}` : `${t('follow')}`}
            </button>
          ) : (
            <Link to="/login" className="public-shop-follow-btn">
              {t('follow')}
            </Link>
          )}
          {followerCount > 0 && (
            <span className="public-shop-follower-count">
              {t('followerCount', { count: followerCount })}
            </span>
          )}
        </div>

        {/* Enter Shop button — opens 3D viewer */}
        {hasViewerData && (
          <button className="public-shop-enter-btn" onClick={handleEnterShop}>
            {t('enterShop')}
          </button>
        )}

        {shop.sections.length > 0 && (
          <div className="public-shop-sections">
            <h3>{t('sections')}</h3>
            <ul>
              {shop.sections
                .sort((a, b) => a.order - b.order)
                .map((sec) => {
                  const datasetId = sectionDatasetMap.get(sec.sectionId);
                  return (
                    <li key={sec.sectionId} className="public-shop-section-row">
                      <span className="public-shop-section-name">{sec.name}</span>
                      {datasetId ? (
                        <button
                          className="public-shop-section-view-btn"
                          onClick={() => handleViewSection(datasetId)}
                        >
                          {t('view')}
                        </button>
                      ) : hasViewerData ? (
                        <span className="public-shop-section-no-scan">{t('noScan')}</span>
                      ) : null}
                    </li>
                  );
                })}
            </ul>
          </div>
        )}

        <div className="public-shop-footer">
          <Link to="/" className="public-shop-home-link">← {t('common:backToHome')}</Link>
          <p>{t('poweredBy')} <Link to="/login">Dukkan</Link></p>
        </div>
      </div>
    </div>
  );
}
